/**
 * 役職の定数
 *
 * 役職(job title)の事実（code・名前・level）の正本は member_role_masters（DB）1 か所。code は3桁数値文字列 "001"〜"008"。
 * ここに持つのは判定で名指しするための code の名前（ROLE_MASTER_CODE）だけ。表示名・色の写しは持たない
 * （DB が読めない時の静的 fallback は置かない・DB との一致は tests/db/role-master-code-db-contract.integration.test.ts が守る）。
 * 認証ロール(permission)の正本は lib/auth/roles.ts。
 */

/** member_role_masters.code の union 型（DB 正本: Issue #4263）*/
export type RoleMasterCode = '001' | '002' | '003' | '004' | '005' | '006' | '007' | '008'

/**
 * 役職マスタ未紐付け（roleMasterCode が null）を表す明示的なセンチネル値。
 *
 * 旧実装は未紐付けの場合 role を 'staff' にデフォルト埋めし、さらに roleGroupCode で
 * '008'（スタッフ）に正規化していたため、本物の '008' ロールの人と役職未設定の人が
 * 画面上で区別できなかった（監査 ADM1-H03）。
 *
 * '001'〜'008' の DB コード・後方互換の英語スラッグ（'staff' 等）のいずれとも衝突しない値にし、
 * roleGroupCode がどのグループにも正規化しない（= その他/未設定として素通しする）ようにする。
 */
export const UNSET_ROLE_CODE = '__unset__'

/** UNSET_ROLE_CODE の表示ラベル。「未設定」であることを明示し「その他」とは区別する。 */
export const UNSET_ROLE_LABEL = '未設定'

/**
 * 役職コード（member_role_masters.code）に名前を付けたもの。判定で役職を名指しする時はここから引く
 * （'003' 等の直書きは lint no-hardcoded-role-code が止める）。
 *
 * 🔴 level ではなく code で見分ける。level 3 には「事業部長」と「事業部長サブ」の2つが居る
 *    （本番 member_role_masters を 2026-10-19 に実測）。level で絞るとサブが混ざる。
 */
export const ROLE_MASTER_CODE = {
  /** 社長（level 0） */
  president: '001',
  /** 役員（level 2） */
  executive: '002',
  /** 事業部長（level 3） */
  divisionHead: '003',
  /** 事業部長サブ（level 3・事業部長とは別の役職） */
  divisionHeadSub: '004',
  /** 部長（level 5） */
  departmentHead: '005',
  /** 課長（level 6） */
  sectionHead: '006',
  /** リーダー（level 7） */
  leader: '007',
  /** スタッフ（level 8） */
  staff: '008',
} as const satisfies Record<string, RoleMasterCode>

/**
 * システム権限（users.role の admin / manager / staff）の表示語。
 *
 * 組織・ユーザー管理の「システム権限」Select（users-panel.tsx）とプロフィール
 * （lib/components/legacy/profile-modal.tsx）の両方がここを引く（表示ソースは単一・2026-09-06）。
 * manager の語「部長」は Select の従来表示を継続する判断（2026-09-06）。「システム権限」に
 * 役職語を出さない思想とは緊張があるので、業務側が別の語を望むならここを変える。
 */
export const SYSTEM_ROLE_LABELS: Record<'admin' | 'manager' | 'staff', string> = {
  admin: '管理者',
  manager: '部長',
  staff: 'スタッフ',
}

/**
 * member_role_masters.level → カラークラス（DB level 駆動）。
 * DB に新役職を追加してもコード変更不要にするため、level からカラーを導出する。
 * level: 0=社長 / 2=役員 / 3=事業部長系 / 5=部長 / 6=課長 / 7=リーダー / 8=スタッフ
 */
export function getRoleColorClassByLevel(level: number): { bg: string; text: string } {
  if (level <= 1) return { bg: 'bg-red-200', text: 'text-red-800' } // 社長・役員上位
  if (level <= 4) return { bg: 'bg-amber-100', text: 'text-amber-800' } // 事業部長系
  if (level <= 7) return { bg: 'bg-yellow-100', text: 'text-yellow-700' } // 部長・課長・リーダー
  return { bg: 'bg-muted', text: 'text-label' } // スタッフ
}

/** 役職バッジの色クラス（DB の level から導出）。level が無い（マスタを読めない・未設定）時は中立色 */
export function getRoleColorClass(level: number | null | undefined): string {
  if (level === null || level === undefined) return 'bg-muted text-label'
  const c = getRoleColorClassByLevel(level)
  return `${c.bg} ${c.text}`
}

/**
 * 役職表示グループ（役職チップの枠）。code は ROLE_MASTER_CODE の値から作る（DB の有効な code との一致は DB 契約の試験が守る）。
 * 名前は DB の member_role_masters.name を読む（ここには持たない）。
 */
export const ROLE_DISPLAY_GROUPS: { label: string; roles: string[] }[] = [
  { label: '役職', roles: Object.values(ROLE_MASTER_CODE) },
]
